import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { LayoutContextValue, LAYOUT_STORAGE_KEYS } from '@/types/layout'

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined)

interface LayoutProviderProps {
  children: ReactNode
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const location = useLocation()
  
  // Load sidebar collapsed state from sessionStorage
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(LAYOUT_STORAGE_KEYS.SIDEBAR_COLLAPSED)
    return stored === 'true'
  })
  
  const [mobileMenuOpen, setMobileMenuOpenState] = useState<boolean>(false)
  const [currentRoute, setCurrentRouteState] = useState<string>(location.pathname)

  // Update current route when location changes
  useEffect(() => {
    setCurrentRouteState(location.pathname)
  }, [location.pathname])

  // Toggle sidebar and persist to sessionStorage
  const toggleSidebar = () => {
    setSidebarCollapsedState((prev) => {
      const newValue = !prev
      sessionStorage.setItem(LAYOUT_STORAGE_KEYS.SIDEBAR_COLLAPSED, String(newValue))
      return newValue
    })
  }

  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed)
    sessionStorage.setItem(LAYOUT_STORAGE_KEYS.SIDEBAR_COLLAPSED, String(collapsed))
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpenState((prev) => !prev)
  }

  const setMobileMenuOpen = (open: boolean) => {
    setMobileMenuOpenState(open)
  }

  const setCurrentRoute = (route: string) => {
    setCurrentRouteState(route)
  }

  const value: LayoutContextValue = {
    sidebarCollapsed,
    mobileMenuOpen,
    currentRoute,
    toggleSidebar,
    setSidebarCollapsed,
    toggleMobileMenu,
    setMobileMenuOpen,
    setCurrentRoute,
  }

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  )
}

export const useLayoutContext = (): LayoutContextValue => {
  const context = useContext(LayoutContext)
  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider')
  }
  return context
}
